import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Download, History, Plus, Trash2, GripVertical, Copy, FilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate } from "@/lib/format";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const STATUS = ["Draft", "Sent", "Accepted", "Rejected", "Expired"];
const UNITS = ["Sq.Ft", "Sq.M", "RMT", "Nos", "Lot"];

const EMPTY_LINE = { description: "", product_system: "", quantity: 0, unit: "Sq.Ft", rate: 0, notes: "" };

export default function QuotationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isNew = id === "new" || !id;
  const canWrite = user?.role !== "ACCOUNTS";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);

  const [form, setForm] = useState({
    title: "", lead_id: "", project_id: "", company_id: "",
    customer: { name: "", address: "", gst_number: "", contact_person: "", phone: "", email: "", site_name: "" },
    quotation_date: new Date().toISOString().slice(0, 10),
    valid_until: "", status: "Draft",
    template_id: "", line_items: [{ ...EMPTY_LINE }],
    discount_pct: 0, discount_amount: 0, additional_charges: 0,
    cgst_pct: 9, sgst_pct: 9,
    payment_terms: "", warranty: "", notes: "",
    content: null,
    show_tax_breakdown: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, ldres, prres, cores] = await Promise.all([
        api.get("/quotation-templates"),
        api.get("/leads?limit=200"),
        api.get("/projects?limit=200"),
        api.get("/companies?limit=200"),
      ]);
      setTemplates(tpls.data || []);
      setLeads(ldres.data.items || []);
      setProjects(prres.data.items || []);
      setCompanies(cores.data.items || []);
      if (isNew) {
        const def = tpls.data.find((t) => t.is_default) || tpls.data[0];
        const preset = location.state || {};
        setForm((f) => ({
          ...f,
          template_id: def?.id || "",
          content: def?.content || null,
          lead_id: preset.lead_id || "",
          project_id: preset.project_id || "",
          company_id: preset.company_id || "",
        }));
      } else {
        const { data } = await api.get(`/quotations/${id}`);
        const q = data.quotation;
        setQuotation(q);
        setRevisions(data.revisions || []);
        setForm({
          title: q.title || "",
          lead_id: q.lead_id || "", project_id: q.project_id || "", company_id: q.company_id || "",
          customer: q.customer || {},
          quotation_date: q.quotation_date || "",
          valid_until: q.valid_until || "",
          status: q.status || "Draft",
          template_id: q.template_id || "",
          line_items: q.line_items?.length ? q.line_items : [{ ...EMPTY_LINE }],
          discount_pct: q.discount_pct || 0, discount_amount: q.discount_amount || 0, additional_charges: q.additional_charges || 0,
          cgst_pct: q.cgst_pct ?? 9, sgst_pct: q.sgst_pct ?? 9,
          payment_terms: q.payment_terms || "", warranty: q.warranty || "", notes: q.notes || "",
          content: q.content,
          show_tax_breakdown: !!q.show_tax_breakdown,
        });
      }
    } finally { setLoading(false); }
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Totals (preview, server recomputes anyway)
  const subtotal = form.line_items.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const discAmt = Number(form.discount_amount || 0) || +(subtotal * (Number(form.discount_pct || 0) / 100)).toFixed(2);
  const taxable = +(subtotal - discAmt + Number(form.additional_charges || 0)).toFixed(2);
  const cgst = +(taxable * Number(form.cgst_pct || 0) / 100).toFixed(2);
  const sgst = +(taxable * Number(form.sgst_pct || 0) / 100).toFixed(2);
  const grand = +(taxable + cgst + sgst).toFixed(2);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({ ...f, template_id: tid, content: structuredClone(t.content || {}) }));
    toast.success(`Template "${t.name}" applied — content reset to template defaults.`);
  };

  const prefillFromLead = async (lid) => {
    if (!lid) { setForm((f) => ({ ...f, lead_id: "" })); return; }
    const l = leads.find((x) => x.id === lid);
    if (!l) return;
    setForm((f) => ({
      ...f, lead_id: lid, project_id: l.project_id || f.project_id,
      company_id: l.company_id || f.company_id,
      title: f.title || `Quotation for ${l.company_name || l.name}`,
      customer: {
        ...f.customer,
        name: l.company_name || f.customer.name,
        contact_person: l.contact_person || f.customer.contact_person,
        phone: l.phone || f.customer.phone,
        email: l.email || f.customer.email,
        site_name: l.location || f.customer.site_name,
      },
      line_items: (l.scope_items && l.scope_items.length) ? l.scope_items.map((s) => ({
        description: s.system || "Dazlac Flooring System",
        product_system: s.system || "", quantity: s.area || 0, unit: "Sq.Ft", rate: s.rate || 0, notes: "",
      })) : f.line_items,
    }));
  };

  const prefillFromProject = async (pid) => {
    if (!pid) { setForm((f) => ({ ...f, project_id: "" })); return; }
    const p = projects.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => ({
      ...f, project_id: pid, company_id: p.company_id || f.company_id,
      title: f.title || `Quotation — ${p.name}`,
      customer: { ...f.customer, name: p.company_name || f.customer.name, site_name: p.location || f.customer.site_name },
      line_items: (p.total_contracted_area && p.quoted_rate) ? [{
        description: p.flooring_system || "Dazlac Flooring System",
        product_system: p.flooring_system || "",
        quantity: p.total_contracted_area, unit: "Sq.Ft", rate: p.quoted_rate, notes: "",
      }] : f.line_items,
    }));
  };

  const updateLine = (i, k, v) => setForm((f) => ({
    ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, [k]: v } : l),
  }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.customer.name) return toast.error("Customer name is required");
    if (!form.line_items.some((l) => l.description || l.quantity)) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = { ...form, line_items: form.line_items.filter((l) => l.description || l.quantity || l.rate) };
      if (isNew) {
        const { data } = await api.post("/quotations", payload);
        toast.success(`Quotation ${data.quotation_number} created`);
        navigate(`/quotations/${data.id}`, { replace: true });
      } else {
        await api.put(`/quotations/${id}`, payload);
        toast.success("Quotation saved");
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const createRevision = async () => {
    if (isNew) return;
    await api.post(`/quotations/${id}/revise`);
    toast.success("Revision snapshot saved");
    load();
  };

  const downloadPdf = async (qid, label) => {
    const target = qid || id;
    if (!target) return;
    const url = label ? `/quotation-revisions/${qid}/pdf` : `/quotations/${target}/pdf`;
    const res = await api.get(url, { responseType: "blob" });
    const blob = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blob; a.download = `${quotation?.quotation_number || "quotation"}${label ? "-" + label : ""}.pdf`; a.click();
    URL.revokeObjectURL(blob);
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const content = form.content || {};

  return (
    <div className="space-y-5 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/quotations")} className="rounded-md p-1.5 hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{isNew ? "New Quotation" : quotation?.quotation_number}</h1>
            <p className="text-xs text-slate-500">{isNew ? "Create a quotation from a lead, project, or company" : `Last updated ${formatDate(quotation?.updated_at)} · rev ${quotation?.revision || 1}`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && <Button variant="outline" onClick={() => setShowRevisions(true)} data-testid="revisions-btn" className="gap-1.5"><History className="h-4 w-4" /> Revisions ({revisions.length})</Button>}
          {!isNew && <Button variant="outline" onClick={() => downloadPdf()} data-testid="download-pdf-btn" className="gap-1.5"><Download className="h-4 w-4" /> Download PDF</Button>}
          {!isNew && canWrite && <Button variant="outline" onClick={createRevision} className="gap-1.5"><FilePlus className="h-4 w-4" /> Save as Revision</Button>}
          {canWrite && <Button onClick={save} disabled={saving} data-testid="save-quotation-btn" className="gap-1.5 font-semibold"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</Button>}
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList data-testid="editor-tabs">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="content">Template Content</TabsTrigger>
          <TabsTrigger value="terms">Terms</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Linked Records">
              <Field label="From Lead">
                <Select value={form.lead_id || "none"} onValueChange={(v) => prefillFromLead(v === "none" ? "" : v)} disabled={!canWrite}>
                  <SelectTrigger data-testid="link-lead"><SelectValue placeholder="Select lead" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.lead_code} · {l.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="From Project">
                <Select value={form.project_id || "none"} onValueChange={(v) => prefillFromProject(v === "none" ? "" : v)} disabled={!canWrite}>
                  <SelectTrigger data-testid="link-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Company">
                <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? "" : v })} disabled={!canWrite}>
                  <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Template">
                <Select value={form.template_id || ""} onValueChange={applyTemplate} disabled={!canWrite}>
                  <SelectTrigger data-testid="link-template"><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </Card>

            <Card title="Customer">
              <Field label="Customer / Company Name *"><Input data-testid="cust-name" value={form.customer.name || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} disabled={!canWrite} /></Field>
              <Field label="Address"><Textarea rows={3} value={form.customer.address || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })} disabled={!canWrite} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GST Number"><Input value={form.customer.gst_number || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, gst_number: e.target.value } })} disabled={!canWrite} /></Field>
                <Field label="Site / Project Name"><Input value={form.customer.site_name || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, site_name: e.target.value } })} disabled={!canWrite} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Contact Person"><Input value={form.customer.contact_person || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, contact_person: e.target.value } })} disabled={!canWrite} /></Field>
                <Field label="Phone"><Input value={form.customer.phone || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} disabled={!canWrite} /></Field>
                <Field label="Email"><Input value={form.customer.email || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })} disabled={!canWrite} /></Field>
              </div>
            </Card>

            <Card title="Meta">
              <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={!canWrite} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Quotation Date"><Input type="date" value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} disabled={!canWrite} /></Field>
                <Field label="Valid Until"><Input type="date" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} disabled={!canWrite} /></Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canWrite}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="mt-4">
          <Card title="Line Items">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs font-semibold uppercase text-slate-500">
                  <tr><th className="px-2 py-2 text-left">Description / System</th><th className="px-2 py-2 text-right w-24">Qty</th><th className="px-2 py-2 w-24">Unit</th><th className="px-2 py-2 text-right w-28">Rate</th><th className="px-2 py-2 text-right w-32">Amount</th><th className="w-8"></th></tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-2"><Input data-testid={`li-desc-${i}`} value={li.description} onChange={(e) => updateLine(i, "description", e.target.value)} disabled={!canWrite} placeholder="Dazlac Hi-Build Epoxy Coating" /></td>
                      <td className="px-2 py-2"><Input type="number" value={li.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} disabled={!canWrite} className="text-right" /></td>
                      <td className="px-2 py-2">
                        <Select value={li.unit} onValueChange={(v) => updateLine(i, "unit", v)} disabled={!canWrite}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2"><Input type="number" value={li.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} disabled={!canWrite} className="text-right" /></td>
                      <td className="px-2 py-2 text-right font-mono font-semibold">{formatINR((Number(li.quantity) || 0) * (Number(li.rate) || 0))}</td>
                      <td className="px-2 py-2">{canWrite && form.line_items.length > 1 && <button onClick={() => removeLine(i)} className="rounded p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canWrite && <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn" className="mt-3 gap-1.5"><Plus className="h-4 w-4" /> Add Line</Button>}

            <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-100 pt-5 md:grid-cols-2">
              <div className="space-y-3">
                <Field label="Discount %"><Input type="number" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value, discount_amount: 0 })} disabled={!canWrite} /></Field>
                <Field label="Additional Charges"><Input type="number" value={form.additional_charges} onChange={(e) => setForm({ ...form, additional_charges: e.target.value })} disabled={!canWrite} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CGST %"><Input type="number" value={form.cgst_pct} onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })} disabled={!canWrite} /></Field>
                  <Field label="SGST %"><Input type="number" value={form.sgst_pct} onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })} disabled={!canWrite} /></Field>
                </div>
                <label className="mt-1 flex items-center gap-2 text-xs font-medium text-slate-600">
                  <input type="checkbox" data-testid="show-tax-breakdown"
                    checked={!!form.show_tax_breakdown}
                    onChange={(e) => setForm({ ...form, show_tax_breakdown: e.target.checked })}
                    disabled={!canWrite} className="h-4 w-4 rounded border-slate-300" />
                  Show tax breakdown on PDF (Taxable / CGST / SGST / Grand Total rows)
                </label>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 text-sm">
                <TotalRow label="Subtotal" value={subtotal} />
                {discAmt > 0 && <TotalRow label="Discount" value={-discAmt} />}
                {Number(form.additional_charges) > 0 && <TotalRow label="Additional Charges" value={Number(form.additional_charges)} />}
                <TotalRow label="Taxable Amount" value={taxable} />
                <TotalRow label={`CGST ${form.cgst_pct}%`} value={cgst} />
                <TotalRow label={`SGST ${form.sgst_pct}%`} value={sgst} />
                <div className="mt-2 border-t border-slate-200 pt-2"><TotalRow label="Grand Total" value={grand} bold /></div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="mt-4">
          <Card title="Template Content (editable per quotation)" description="These fields fill the 3-page Dazlac PDF. Changing them here does not affect the master template.">
            <div className="grid gap-4 lg:grid-cols-2">
              <Field label="Cover — Title Line 1"><Input value={content.cover?.title_line_1 || ""} onChange={(e) => setContentPath(setForm, "cover.title_line_1", e.target.value)} disabled={!canWrite} /></Field>
              <Field label="Cover — Title Line 2"><Input value={content.cover?.title_line_2 || ""} onChange={(e) => setContentPath(setForm, "cover.title_line_2", e.target.value)} disabled={!canWrite} /></Field>
              <Field label="Cover — Title Line 3"><Input value={content.cover?.title_line_3 || ""} onChange={(e) => setContentPath(setForm, "cover.title_line_3", e.target.value)} disabled={!canWrite} /></Field>
              <Field label="Pricing Table Title"><Input value={content.pricing_table_title || ""} onChange={(e) => setContentPath(setForm, "pricing_table_title", e.target.value)} disabled={!canWrite} /></Field>
              <Field label="Intro Salutation"><Input value={content.intro_salutation || ""} onChange={(e) => setContentPath(setForm, "intro_salutation", e.target.value)} disabled={!canWrite} /></Field>
              <Field label="GST Note"><Input value={content.gst_note || ""} onChange={(e) => setContentPath(setForm, "gst_note", e.target.value)} disabled={!canWrite} /></Field>
            </div>
            <Field label="Intro Paragraph" className="mt-3"><Textarea rows={3} value={content.intro_text || ""} onChange={(e) => setContentPath(setForm, "intro_text", e.target.value)} disabled={!canWrite} /></Field>
            <Field label="Certification Paragraph"><Textarea rows={2} value={content.certification_text || ""} onChange={(e) => setContentPath(setForm, "certification_text", e.target.value)} disabled={!canWrite} /></Field>
            <Field label="Methodology Title"><Input value={content.methodology_title || ""} onChange={(e) => setContentPath(setForm, "methodology_title", e.target.value)} disabled={!canWrite} /></Field>
            <RepeatableList label="Methodology Steps" items={content.methodology_steps || []} canWrite={canWrite}
              onChange={(arr) => setContentPath(setForm, "methodology_steps", arr)} />
            <Field label="Methodology Footnote"><Input value={content.methodology_footnote || ""} onChange={(e) => setContentPath(setForm, "methodology_footnote", e.target.value)} disabled={!canWrite} /></Field>
            <Field label="Closing Statement"><Input value={content.closing || ""} onChange={(e) => setContentPath(setForm, "closing", e.target.value)} disabled={!canWrite} /></Field>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <KvEditor title="Further Information" items={content.further_info || []} canWrite={canWrite}
                onChange={(arr) => setContentPath(setForm, "further_info", arr)} />
              <KvEditor title="Bank Details" items={content.bank_details || []} canWrite={canWrite}
                onChange={(arr) => setContentPath(setForm, "bank_details", arr)} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="terms" className="mt-4">
          <Card title="Terms & Conditions (per quotation)" description="Add, remove, reorder or rewrite any clause. Default clauses come from the chosen template.">
            <RepeatableList items={content.terms || []} canWrite={canWrite} rows={3}
              onChange={(arr) => setContentPath(setForm, "terms", arr)} testid="terms-list" />
          </Card>
        </TabsContent>
      </Tabs>

      <Sheet open={showRevisions} onOpenChange={setShowRevisions}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetTitle>Revision History — {quotation?.quotation_number}</SheetTitle>
          <div className="mt-5 space-y-3">
            {revisions.length === 0 && <p className="text-sm text-slate-500">No prior revisions. Use "Save as Revision" to snapshot the current state before making major changes.</p>}
            {revisions.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="font-semibold text-slate-800">Revision {r.revision}</div>
                  <div className="text-xs text-slate-500">{formatDate(r.created_at)} · by {r.created_by_name} · total {formatINR(r.snapshot?.grand_total)}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, `rev${r.revision}`)} className="gap-1.5"><Download className="h-4 w-4" /> PDF</Button>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function setContentPath(setForm, path, value) {
  setForm((f) => {
    const content = structuredClone(f.content || {});
    const keys = path.split(".");
    let cur = content;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return { ...f, content };
  });
}

function Card({ title, description, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="text-base font-semibold text-slate-800">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-xs font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between py-0.5 ${bold ? "text-base font-bold" : "text-sm"}`}>
      <span className="text-slate-600">{label}</span>
      <span className="font-mono text-slate-900">{formatINR(value)}</span>
    </div>
  );
}

function RepeatableList({ label, items, onChange, canWrite, rows = 2, testid }) {
  return (
    <div>
      {label && <Label className="mb-1 block text-xs font-semibold text-slate-600">{label}</Label>}
      <div className="space-y-2" data-testid={testid}>
        {items.map((text, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-2 w-5 shrink-0 text-right text-xs font-semibold text-slate-400">{i + 1}.</span>
            <Textarea rows={rows} value={text} onChange={(e) => onChange(items.map((t, idx) => idx === i ? e.target.value : t))} disabled={!canWrite} />
            {canWrite && (
              <div className="flex flex-col gap-1">
                <button onClick={() => { if (i > 0) onChange(swap(items, i, i - 1)); }} className="text-xs text-slate-400 hover:text-slate-700">↑</button>
                <button onClick={() => { if (i < items.length - 1) onChange(swap(items, i, i + 1)); }} className="text-xs text-slate-400 hover:text-slate-700">↓</button>
                <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {canWrite && <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="mt-2 gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>}
    </div>
  );
}

function normalizeKvItems(items) {
  if (Array.isArray(items)) {
    return items.map((it) => {
      if (it && typeof it === "object" && !Array.isArray(it)) {
        return { label: it.label ?? "", value: it.value ?? "" };
      }
      return { label: "", value: it == null ? "" : String(it) };
    });
  }
  if (items && typeof items === "object") {
    return Object.entries(items).map(([k, v]) => ({
      label: String(k)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      value: v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v),
    }));
  }
  return [];
}

function KvEditor({ title, items, onChange, canWrite }) {
  const normalized = normalizeKvItems(items);
  // If upstream passed a non-array (e.g. an object), surface the normalized
  // array back so subsequent edits persist as the expected shape.
  const safeItems = normalized;
  const emit = (next) => onChange(next);
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 text-sm font-semibold text-slate-700">{title}</div>
      <div className="space-y-2">
        {safeItems.map((kv, i) => (
          <div key={i} className="flex items-start gap-2">
            <Input className="w-32" value={kv.label || ""} onChange={(e) => emit(safeItems.map((it, idx) => idx === i ? { ...it, label: e.target.value } : it))} placeholder="Label" disabled={!canWrite} />
            <Textarea rows={1} value={kv.value || ""} onChange={(e) => emit(safeItems.map((it, idx) => idx === i ? { ...it, value: e.target.value } : it))} placeholder="Value" disabled={!canWrite} />
            {canWrite && <button onClick={() => emit(safeItems.filter((_, idx) => idx !== i))} className="mt-2 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>
      {canWrite && <Button variant="outline" size="sm" onClick={() => emit([...safeItems, { label: "", value: "" }])} className="mt-2 gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>}
    </div>
  );
}

function swap(arr, i, j) { const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c; }
